import { useEffect, useState, type ReactNode } from 'react';
import { Alert, CircularProgress, DialogContent, Stack } from '@mui/material';
import { createTeamInvitation, listInvitationUnits } from '../../lib/team-api';
import { parseResidentImport, residentTemplateCsv, type InviteUnit } from '../../lib/resident-import';
import { extractApiError } from '../../lib/api-client';
import { unitAddress } from '../../lib/unit-address';
import { CsvImportPanel } from '../../components/CsvImportPanel';
import { useT } from '../../i18n';

/**
 * The "Impor massal" tab of the invite dialog: one resident per row with their
 * unit, each sent the same invitation as the single form.
 */
export function ResidentImportPanel({
  developerId,
  clusterId,
  clusterPicker,
  existingEmails,
  onClose,
  onImported,
}: {
  developerId: string;
  clusterId: string | null;
  /** Lets someone with several clusters choose where the residents go. */
  clusterPicker?: ReactNode;
  existingEmails: string[];
  onClose: () => void;
  onImported: () => void;
}) {
  const t = useT();
  const [units, setUnits] = useState<InviteUnit[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The rows are matched against this cluster's units, so load them first.
  useEffect(() => {
    setUnits(null);
    setError(null);
    if (!clusterId) return;
    let cancelled = false;
    listInvitationUnits(clusterId)
      .then((items) => { if (!cancelled) setUnits(items); })
      .catch((err: unknown) => { if (!cancelled) setError(extractApiError(err).message); });
    return () => { cancelled = true; };
  }, [clusterId]);

  if (error || !units) {
    return (
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {clusterPicker}
          {error ? <Alert severity="error">{error}</Alert> : <CircularProgress size={24} />}
        </Stack>
      </DialogContent>
    );
  }

  return (
    <CsvImportPanel
      // A new cluster means new units to match against: start over.
      key={clusterId ?? ''}
      templateFile="template-undang-warga.csv"
      templateCsv={residentTemplateCsv}
      intro={clusterPicker}
      step1Help={t('res.import.step1Help')}
      parse={(text) => {
        const res = parseResidentImport(text, units, existingEmails);
        if (res.ok) return res.rows;
        return t(res.error === 'empty' ? 'import.fileEmpty' : 'res.import.fileColumns');
      }}
      columns={[
        { label: t('team.fullName'), render: (r) => r.name || '—' },
        { label: t('team.email'), render: (r) => r.email || '—' },
        { label: t('res.colUnit'), render: (r) => <span style={{ whiteSpace: 'nowrap' }}>{unitAddress(r.block, r.number) || '—'}</span> },
      ]}
      describeRow={(r) => r.email}
      send={(r) => createTeamInvitation({
        developer_id: developerId,
        cluster_id: clusterId!,
        full_name: r.name,
        email: r.email,
        role: 'resident',
        unit_id: r.unitId!,
      })}
      runLabel={(count) => t('res.import.run', { count })}
      sentLabel={(count) => t('res.import.sent', { count })}
      onClose={onClose}
      onImported={onImported}
    />
  );
}
